import { DrawerBody, VStack } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';

import { drawerRunActions } from './domainActions';
import { MeshSelect } from './MeshSelect';
import { RunAgain } from './RunAgain';
import { UploadContainer } from './UploadContainer';
import { DrawerProgressBar } from 'packages/dataPipeline/ui/uploads/DrawerProgressBar';
import { DateAndVersionSelector } from 'packages/dataPipeline/ui/uploads/pipelinePicker/DateAndVersionSelector';
import { UploadContextProvider } from '~/packages/dataPipeline/hooks/UploadContext';
import {
  MeshRunParams,
  RDFV,
  SelectedMeshRun,
} from '~/packages/dataPipeline/types/dataPipelineTypes';

// reusable upload + actions block
const UploadActionsSection = ({
  organizationId,
  meshRunParams,
  onRunStarted,
  onRunAgain,
}: {
  organizationId: string;
  meshRunParams: MeshRunParams;
  onRunStarted: (() => void) | null;
  onRunAgain: ((fileVersion: number) => void) | null;
}) => (
  <UploadContextProvider
    organizationId={organizationId}
    meshRunId={null}
    meshRunParams={meshRunParams}
    poll
    onRunStarted={onRunStarted}
  >
    <UploadContainer organizationId={organizationId} />
    {onRunAgain && <RunAgain onRunAgain={onRunAgain} />}
    {drawerRunActions.map(({ key, Component }) => (
      <Component key={key} />
    ))}
  </UploadContextProvider>
);

// reusable progress bar block
const UploadProgressSection = ({
  organizationId,
  meshRunParams,
}: {
  organizationId: string;
  meshRunParams: MeshRunParams;
}) => (
  <UploadContextProvider
    organizationId={organizationId}
    meshRunId={null}
    meshRunParams={meshRunParams}
    // the upload section's provider polls this run; this one reads the cache.
    poll={false}
    onRunStarted={null}
  >
    <DrawerProgressBar />
  </UploadContextProvider>
);

// the drawer body: the mesh, the date and version, and, once both are chosen,
// the uploads and actions of that run. A null callback shows its choice fixed;
// a null onRunAgain offers no "Run again".
const UploadForMeshRun = ({
  organizationId,
  meshDocuments,
  selectedMeshDocument,
  onMeshSelected,
  selectedRDFV,
  onRDFVSelected,
  onRunStarted,
  onRunAgain,
}: {
  organizationId: string;
  meshDocuments: DataPipeline.MeshDocumentMetadata[];
  selectedMeshDocument: DataPipeline.MeshDocumentMetadata | null;
  onMeshSelected:
    | ((meshDocument: DataPipeline.MeshDocumentMetadata | null) => void)
    | null;
  selectedRDFV: RDFV | null;
  onRDFVSelected: ((rdfv: RDFV | null) => void) | null;
  onRunStarted: (() => void) | null;
  onRunAgain: ((fileVersion: number) => void) | null;
}): React.ReactElement => {
  const meshRunParams: MeshRunParams | null =
    selectedMeshDocument && selectedRDFV
      ? {
          meshDocumentMetadata: selectedMeshDocument,
          rdfv: selectedRDFV,
        }
      : null;
  return (
    <>
      <DrawerBody>
        <VStack spacing="20px" alignItems="flex-start">
          {/* select credit facility/mesh */}
          <MeshSelect
            selectedMeshDocumentId={
              selectedMeshDocument?.meshDocumentId ?? null
            }
            onMeshSelected={onMeshSelected}
            meshDocuments={meshDocuments}
          />
          {selectedMeshDocument && (
            <DateAndVersionSelector
              implementationId={selectedMeshDocument.implementationId}
              selectedRDFV={selectedRDFV}
              // only a new version's run is yet to start
              isNewVersion={onRunStarted !== null}
              setSelectedRDFV={onRDFVSelected}
            />
          )}
          {meshRunParams && (
            // show upload tab only when we have selected mesh params
            <UploadActionsSection
              organizationId={organizationId}
              meshRunParams={meshRunParams}
              onRunStarted={onRunStarted}
              onRunAgain={onRunAgain}
            />
          )}
        </VStack>
      </DrawerBody>
      {meshRunParams && (
        // show progress bar only when upload tab is active
        <UploadProgressSection
          organizationId={organizationId}
          meshRunParams={meshRunParams}
        />
      )}
    </>
  );
};

/**
 * Upload for a new pipeline run
 * This component allows the user to select a mesh document and RDFV,
 * and then upload data against the selected mesh.
 * */
export const UploadForNewPipeline = ({
  organizationId,
  rdfv,
  latestMeshDocuments,
}: {
  organizationId: string;
  rdfv: RDFV | null;
  latestMeshDocuments: DataPipeline.MeshDocumentMetadata[];
}): React.ReactElement => {
  // upload drawer lets you choose mesh to upload against
  // when selectedMesh is provided, we restrict the choice to that mesh
  const [
    selectedMeshDocument,
    setSelectedMeshDocument,
  ] = useState<DataPipeline.MeshDocumentMetadata | null>(
    latestMeshDocuments.length ? latestMeshDocuments[0] : null,
  );
  const [selectedRDFV, setSelectedRDFV] = useState<RDFV | null>(rdfv ?? null);

  useEffect(() => {
    // reset rdfv if selectedMeshDocument changes
    // unless we are given rdfv as prop
    if (!rdfv) {
      setSelectedRDFV(null);
    }
  }, [rdfv, selectedMeshDocument]);

  // the version shown is new until an upload starts it: the first upload starts it
  // (or fails if someone else just did), the rest fill it in
  const [runStarted, setRunStarted] = useState<boolean>(false);
  const onRunStarted = useCallback((): void => setRunStarted(true), []);

  // you can't change the mesh, date or version once the date is chosen.
  // to reset the selection, you need to click "New Report"
  return (
    <UploadForMeshRun
      organizationId={organizationId}
      meshDocuments={latestMeshDocuments}
      selectedMeshDocument={selectedMeshDocument}
      onMeshSelected={selectedRDFV ? null : setSelectedMeshDocument}
      selectedRDFV={selectedRDFV}
      onRDFVSelected={selectedRDFV ? null : setSelectedRDFV}
      onRunStarted={runStarted ? null : onRunStarted}
      onRunAgain={null}
    />
  );
};

/**
 * Upload for an existing pipeline run
 * This component allows the user to upload data against an existing mesh run.
 * The mesh and RDFV are already selected based on the selectedMeshRun.
 * */
export const UploadForExistingPipeline = ({
  organizationId,
  selectedMeshRun,
}: {
  organizationId: string;
  selectedMeshRun: SelectedMeshRun;
}): React.ReactElement => {
  const { meshDocumentMetadata, rdfv } = selectedMeshRun.meshRunParams;
  // once run again, the drawer shows the version the files were kept into
  const [fileVersion, setFileVersion] = useState<number>(rdfv.fileVersion);
  return (
    <UploadForMeshRun
      key={fileVersion}
      organizationId={organizationId}
      meshDocuments={[meshDocumentMetadata]}
      selectedMeshDocument={meshDocumentMetadata}
      onMeshSelected={null}
      selectedRDFV={{ ...rdfv, fileVersion }}
      onRDFVSelected={null}
      // the run exists: an upload fills it in
      onRunStarted={null}
      onRunAgain={setFileVersion}
    />
  );
};
